import React, { useState, useEffect, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@mui/styles';

import Popover from '@mui/material/Popover';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
// import Icon from '@mui/material/Icon';
import { Icon } from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import useWindowSize from '../../libraries/usewindowsize';
// import withWhyRender from '../../libraries/withwhyrender';

import Tooltip from './../styled/tooltip';
import StyledPaper from './../styled/paper';
import { GetContrastText } from '../../d3/utils';
import DataContext from '../../data/data-context.js';
import Auth from '../../data/auth-service.js';
import TagEditor from './tageditor';

const useStyles = makeStyles(theme => ({
    root: {
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        zIndex: theme.zIndex.drawer,
        '& .MuiIcon-root': {
            fontSize: '1.25rem',
        }
    },
    divider: {
        height: 1,
    },
    paper: {
        maxWidth: 237,
        '& .MuiListItemIcon-root': {
            minWidth: 0,
        }
    },
    noicon: {
        borderRadius: 5,
        display: 'inline',
        fontSize: 'small',
        padding: '3px 7px',
        width: 120,
        textAlign: 'right',
        marginRight: 5,
    },
    wtf: {
        fontFamily: 'Material Icons',
    },
}));

const Tag = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const { height } = useWindowSize();
    const [anchorEl, setAnchorEl] = useState(null);
    const { tags, upsertTag, showTag, updateShowTag } = useContext(DataContext);
    const alertPerms = Auth.permissions.get('Alerts');
    const [readonly] = useState(!alertPerms.write);

    const [name, setName] = useState();
    const [color, setColor] = useState();
    const [icon, setIcon] = useState();

    const handleClick = useCallback((event) => {
        setAnchorEl(event.currentTarget);
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const handleClose = useCallback((event) => {
        setAnchorEl(null);
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const handleSelectColor = useCallback((c) => {
        setColor(c);
        upsertTag({ color: c, icon, name });
    }, [upsertTag, icon, name]);

    const handleSelectIcon = useCallback((i) => {
        setIcon(i);
        upsertTag({ color, icon: i, name });
    }, [upsertTag, color, name]);

    const handleOnUntag = useCallback((e) => {
        props.onUntag && props.onUntag(props.tag);
        handleClose(e);
    }, [handleClose, props]);

    const handleShowTagChange = e => {
        updateShowTag(e.target.checked);
    };

    const getHeight = useCallback(() => {
        return Math.min(height / 4, 450);
    }, [height]);

    // incoming
    useEffect(() => {
        const t = tags[props.tag] || { name: props.tag };
        setName(t.name);
        setColor(t.color);
        setIcon(t.icon);
    }, [props.tag, tags]);

    return (
        <React.Fragment>
            <Tooltip title={name || ''} arrow placement="top">
                <span
                    className={classes.root}
                    style={{ color: color }}
                    onClick={readonly || props.noclick ? undefined : handleClick}>
                    {icon && !showTag
                        ? <Icon fontSize="small">{icon}</Icon>
                        : <div
                            className={classes.noicon}
                            style={{
                                color: GetContrastText(color, theme),
                                backgroundColor: color || theme.palette.secondary.main
                            }}>{name}
                        </div>}
                </span>
            </Tooltip>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}>
                <StyledPaper className={classes.paper}>
                    {alertPerms.delete && props.onUntag &&
                        <Button
                            onClick={e => handleOnUntag(e)}
                            className={classes.iconButton}
                            aria-label={props.untagLabel || 'untag'}>
                            <RemoveIcon fontSize='small'/>
                            {props.untagLabel || 'untag'}
                        </Button>
                    }
                    <FormControlLabel
                        size="small"
                        control={<Checkbox size="small" checked={showTag} onChange={handleShowTagChange}/>}
                        label={'Show Tag Label'}
                        aria-label={'Show Tag Label'}/>
                    <Divider className={classes.divider} orientation="horizontal" />
                    <TagEditor
                        color={color}
                        icon={icon}
                        onSelectColor={handleSelectColor}
                        onSelectIcon={handleSelectIcon}
                        height={getHeight()}/>
                </StyledPaper>
            </Popover>
        </React.Fragment>
    );
};
Tag.propTypes = {
    tag: PropTypes.string.isRequired,
    untagLabel: PropTypes.string,
    onUntag: PropTypes.func,
    placement: PropTypes.string,
    noclick: PropTypes.bool,
};

// TODO: can't get props to stop updating so using memo here will just be more expensive
// export default React.memo(Tag);
// export default withWhyRender(Tag); -- nothing changes. wtf?
export default Tag;
